import type { gsap as GsapNamespace } from 'gsap';

type GsapInstance = typeof GsapNamespace;
type ScrollTriggerStatic = typeof import('gsap/ScrollTrigger').ScrollTrigger;

export const SCROLL_REVEAL_START = 'top 88%';
export const SCROLL_REVEAL_TOGGLE_ACTIONS = 'play none none none';
export const SCROLL_REVEAL_IO_ROOT_MARGIN = '0px 0px -12% 0px';

export const SCROLL_REVEAL_SELECTOR = '.reveal-on-scroll, .contact-reveal, .client-reveal, .footer-reveal';

export const SCROLL_REVEAL_FROM_BY_TYPE: Record<string, Record<string, string | number>> = {
  headline: { autoAlpha: 0, y: 38, clipPath: 'inset(0% 0% 100% 0%)' },
  'section-head': { autoAlpha: 0, y: 34, filter: 'blur(3px)' },
  text: { autoAlpha: 0, y: 28, clipPath: 'inset(0% 0% 100% 0%)' },
  card: { autoAlpha: 0, y: 54, scale: 0.965, filter: 'blur(5px)' },
  media: { autoAlpha: 0, y: 32, scale: 0.94, filter: 'blur(4px)', clipPath: 'inset(8% 0% 8% 0% round 16px)' },
  icon: { autoAlpha: 0, y: 20, scale: 0.84 },
  button: { autoAlpha: 0, y: 22, scale: 0.9 },
  logo: { autoAlpha: 0, y: 18, scale: 0.86 },
  line: { autoAlpha: 1, scaleX: 0, transformOrigin: 'left center' }
};

export function getScrollRevealTriggerConfig(
  trigger: Element,
  overrides: Record<string, unknown> = {}
): Record<string, unknown> {
  return {
    trigger,
    start: SCROLL_REVEAL_START,
    end: 'bottom 18%',
    invalidateOnRefresh: true,
    toggleActions: SCROLL_REVEAL_TOGGLE_ACTIONS,
    once: true,
    ...overrides
  };
}

export function markRevealedElements(gsap: GsapInstance, elements: HTMLElement[]): void {
  if (!elements.length) return;

  gsap.set(elements, {
    autoAlpha: 1,
    x: 0,
    y: 0,
    scale: 1,
    scaleX: 1,
    filter: 'blur(0px)',
    clipPath: 'inset(0% 0% 0% 0% round 0px)'
  });
  elements.forEach((element) => {
    element.classList.add('is-revealed');
    delete element.dataset['revealing'];
  });
}

function getRevealFromVars(item: HTMLElement): Record<string, string | number> {
  const type = item.dataset['reveal'] ?? 'text';
  return SCROLL_REVEAL_FROM_BY_TYPE[type] ?? SCROLL_REVEAL_FROM_BY_TYPE['text'];
}

function getRevealDelay(item: HTMLElement): number {
  const order = Number.parseFloat(item.style.getPropertyValue('--reveal-order') || '0');
  return Number.isFinite(order) ? Math.min(0.56, order * 0.075) : 0;
}

function shouldAnimateReveal(item: HTMLElement): boolean {
  return !item.classList.contains('is-revealed') && item.dataset['revealing'] !== '1';
}

/** True when another reveal target wraps this element — avoids hiding text inside cards. */
export function isNestedRevealElement(element: HTMLElement): boolean {
  const parentReveal = element.parentElement?.closest(SCROLL_REVEAL_SELECTOR);
  return !!parentReveal && parentReveal !== element;
}

function revealNestedDescendants(gsap: GsapInstance, container: HTMLElement): void {
  const nested = Array.from(container.querySelectorAll<HTMLElement>(SCROLL_REVEAL_SELECTOR)).filter(
    (element) => element !== container
  );
  if (nested.length) {
    markRevealedElements(gsap, nested);
  }
}

function releaseNestedRevealTargets(gsap: GsapInstance, root: HTMLElement): void {
  Array.from(root.querySelectorAll<HTMLElement>(SCROLL_REVEAL_SELECTOR))
    .filter(isNestedRevealElement)
    .forEach((element) => {
      element.classList.remove('reveal-on-scroll', 'contact-reveal', 'client-reveal', 'footer-reveal');
      delete element.dataset['reveal'];
      delete element.dataset['revealing'];
      delete element.dataset['revealTrigger'];
      element.style.removeProperty('--reveal-order');
      gsap.set(element, { clearProps: 'opacity,visibility,transform,filter,clip-path,scale' });
      element.classList.add('is-revealed');
    });
}

function isElementInRevealZone(element: HTMLElement, thresholdRatio = 0.9): boolean {
  if (typeof window === 'undefined') return false;

  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  if (!viewportHeight) return false;

  const rect = element.getBoundingClientRect();
  return rect.top <= viewportHeight * thresholdRatio && rect.bottom > 8;
}

export function playScrollRevealAnimation(gsap: GsapInstance, item: HTMLElement): void {
  if (!shouldAnimateReveal(item)) return;

  item.dataset['revealing'] = '1';
  const type = item.dataset['reveal'] ?? 'text';
  const fromVars = getRevealFromVars(item);
  const delay = getRevealDelay(item);

  gsap.fromTo(
    item,
    fromVars,
    {
      autoAlpha: 1,
      y: 0,
      scale: 1,
      scaleX: 1,
      filter: 'blur(0px)',
      clipPath: type === 'line' ? undefined : 'inset(0% 0% 0% 0% round 0px)',
      duration: type === 'card' || type === 'media' ? 0.95 : 0.78,
      delay,
      ease: 'expo.out',
      overwrite: 'auto',
      onComplete: () => {
        item.classList.add('is-revealed');
        delete item.dataset['revealing'];
        revealNestedDescendants(gsap, item);
      }
    }
  );
}

/** Force visible state for elements stuck hidden after layout shifts. */
export function revealElementsAlreadyInView(
  gsap: GsapInstance,
  elements: HTMLElement[],
  thresholdRatio = 0.92
): void {
  if (typeof window === 'undefined' || !elements.length) return;

  const stuck: HTMLElement[] = [];

  elements.forEach((element) => {
    if (element.classList.contains('is-revealed')) return;
    if (!isElementInRevealZone(element, thresholdRatio)) return;
    stuck.push(element);
  });

  if (stuck.length) {
    markRevealedElements(gsap, stuck);
    stuck.forEach((element) => revealNestedDescendants(gsap, element));
  }
}

export function scheduleScrollTriggerRefreshes(
  ScrollTrigger: ScrollTriggerStatic,
  onAfterRefresh?: () => void
): () => void {
  const timers: number[] = [];

  const refresh = () => {
    try {
      ScrollTrigger.refresh(true);
      onAfterRefresh?.();
    } catch {
      requestAnimationFrame(() => {
        try {
          ScrollTrigger.refresh(true);
          onAfterRefresh?.();
        } catch {
          // ScrollTrigger can be mid-layout; a later refresh will recover.
        }
      });
    }
  };

  requestAnimationFrame(refresh);
  timers.push(window.setTimeout(refresh, 250) as unknown as number);
  timers.push(window.setTimeout(refresh, 700) as unknown as number);
  timers.push(window.setTimeout(refresh, 1400) as unknown as number);
  timers.push(window.setTimeout(refresh, 2800) as unknown as number);

  return () => timers.forEach((timer) => clearTimeout(timer));
}

export async function refreshGlobalScrollTriggers(): Promise<void> {
  try {
    const { ScrollTrigger } = await import('gsap/ScrollTrigger');
    ScrollTrigger.refresh(true);
  } catch {
    // GSAP may not be loaded on this route yet.
  }
}

export function bindRevealImageLoadRefresh(
  root: HTMLElement,
  cleanups: Array<() => void>
): void {
  const pendingImages = Array.from(root.querySelectorAll<HTMLImageElement>('img')).filter((image) => !image.complete);
  if (!pendingImages.length) return;

  const refresh = () => void refreshGlobalScrollTriggers();

  pendingImages.forEach((image) => {
    image.addEventListener('load', refresh, { once: true });
    image.addEventListener('error', refresh, { once: true });
    cleanups.push(() => {
      image.removeEventListener('load', refresh);
      image.removeEventListener('error', refresh);
    });
  });
}

function revealStuckSectionHeadings(gsap: GsapInstance, root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>('.modern-section, .dingo-section').forEach((section) => {
    const rect = section.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    if (!viewportHeight || rect.bottom <= 0 || rect.top >= viewportHeight) return;

    const heads = Array.from(
      section.querySelectorAll<HTMLElement>('.section-head.reveal-on-scroll:not(.is-revealed), .home-section-head.reveal-on-scroll:not(.is-revealed)')
    );
    if (heads.length) {
      markRevealedElements(gsap, heads);
      heads.forEach((head) => revealNestedDescendants(gsap, head));
    }

    section
      .querySelectorAll<HTMLElement>(
        '.home-section-kicker.reveal-on-scroll:not(.is-revealed), .home-section-title.reveal-on-scroll:not(.is-revealed), .home-section-desc.reveal-on-scroll:not(.is-revealed)'
      )
      .forEach((element) => markRevealedElements(gsap, [element]));
  });
}

export function attachScrollRevealFallback(
  gsap: GsapInstance,
  elements: HTMLElement[],
  cleanups: Array<() => void>,
  root?: HTMLElement
): void {
  const refreshFallback = () => {
    revealElementsAlreadyInView(gsap, elements);
    if (root) {
      revealStuckSectionHeadings(gsap, root);
    }
  };

  let scrollRaf = 0;
  const onScroll = () => {
    if (scrollRaf) return;
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = 0;
      refreshFallback();
    });
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  cleanups.push(() => window.removeEventListener('scroll', onScroll));
  requestAnimationFrame(refreshFallback);
  window.setTimeout(refreshFallback, 400);
  window.setTimeout(refreshFallback, 1200);
  window.setTimeout(refreshFallback, 3000);
}

export function attachScrollRevealIntersectionObserver(
  gsap: GsapInstance,
  elements: HTMLElement[],
  cleanups: Array<() => void>
): void {
  if (typeof window === 'undefined' || !elements.length) return;

  const pending = elements.filter((item) => shouldAnimateReveal(item));
  if (!pending.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        playScrollRevealAnimation(gsap, entry.target as HTMLElement);
        observer.unobserve(entry.target);
      });
    },
    {
      root: null,
      rootMargin: SCROLL_REVEAL_IO_ROOT_MARGIN,
      threshold: 0.05
    }
  );

  pending.forEach((item) => {
    if (isElementInRevealZone(item)) {
      playScrollRevealAnimation(gsap, item);
      return;
    }
    observer.observe(item);
  });

  cleanups.push(() => observer.disconnect());
}

export function setupScrollRevealForRoot(
  gsap: GsapInstance,
  ScrollTrigger: ScrollTriggerStatic,
  root: HTMLElement,
  options: {
    selector?: string;
    excludeClosest?: string[];
    cleanups?: Array<() => void>;
  } = {}
): void {
  const selector = options.selector ?? SCROLL_REVEAL_SELECTOR;
  const excludeClosest = options.excludeClosest ?? ['.presentation-slider', '.approach-timeline'];

  releaseNestedRevealTargets(gsap, root);

  const revealItems = Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
    (item) =>
      !excludeClosest.some((selectorFragment) => item.closest(selectorFragment)) &&
      !isNestedRevealElement(item)
  );

  if (!revealItems.length) return;

  const reduceMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (reduceMotion) {
    markRevealedElements(gsap, revealItems);
    return;
  }

  const immediateItems = revealItems.filter((item) => item.dataset['revealTrigger'] === 'immediate');
  const scrollItems = revealItems.filter((item) => item.dataset['revealTrigger'] !== 'immediate');

  scrollItems.forEach((item) => {
    if (item.classList.contains('is-revealed')) return;
    gsap.set(item, getRevealFromVars(item));
  });

  immediateItems.forEach((item) => playScrollRevealAnimation(gsap, item));

  const cleanups = options.cleanups ?? [];
  attachScrollRevealIntersectionObserver(gsap, scrollItems, cleanups);

  const refreshFallback = () => revealElementsAlreadyInView(gsap, scrollItems);
  const cancelScheduledRefreshes = scheduleScrollTriggerRefreshes(ScrollTrigger, refreshFallback);
  cleanups.push(cancelScheduledRefreshes);
  attachScrollRevealFallback(gsap, scrollItems, cleanups, root);
}

export async function initScrollRevealOnRoot(
  root: HTMLElement,
  cleanups: Array<() => void>,
  options: {
    selector?: string;
    excludeClosest?: string[];
  } = {}
): Promise<() => void> {
  const [{ gsap }, { ScrollTrigger }] = await Promise.all([
    import('gsap'),
    import('gsap/ScrollTrigger')
  ]);

  gsap.registerPlugin(ScrollTrigger);

  const ctx = gsap.context(() => {
    setupScrollRevealForRoot(gsap, ScrollTrigger, root, {
      selector: options.selector,
      excludeClosest: options.excludeClosest,
      cleanups
    });
  }, root);

  const cleanup = () => ctx.revert();
  cleanups.push(cleanup);
  return cleanup;
}

/** Standard page init: IO reveals + image refresh + ScrollTrigger sync. */
export async function initPageScrollReveal(
  root: HTMLElement,
  cleanups: Array<() => void>,
  options: {
    selector?: string;
    excludeClosest?: string[];
  } = {}
): Promise<void> {
  await initScrollRevealOnRoot(root, cleanups, options);
  bindRevealImageLoadRefresh(root, cleanups);
  await refreshGlobalScrollTriggers();
}

export const LEGACY_SERVICE_DETAIL_SELECTOR =
  'app-publicity, app-web-development, app-digital-marketing, app-digital-media-center, app-social-media, app-app-development, app-data-science, app-blockchain, app-call-center, app-business-intelligence, app-fintech';

export function prepareLegacyServiceDetailRevealElements(root: HTMLElement): void {
  const tagSectionHead = (
    element: HTMLElement | null | undefined,
    options: { immediate?: boolean; order?: number } = {}
  ): void => {
    if (!element || element.classList.contains('section-head')) return;

    element.classList.add('section-head', 'reveal-on-scroll');
    element.dataset['reveal'] = 'section-head';
    if (options.immediate) {
      element.dataset['revealTrigger'] = 'immediate';
    }
    if (options.order !== undefined && !element.style.getPropertyValue('--reveal-order')) {
      element.style.setProperty('--reveal-order', String(options.order));
    }
  };

  tagSectionHead(root.querySelector<HTMLElement>('header.masthead .col-12'), { immediate: true });
  tagSectionHead(root.querySelector<HTMLElement>('.modern-service-hero-section .col-lg-7'), { immediate: true });
  tagSectionHead(root.querySelector<HTMLElement>('.modern-service-why-section .row > .col-md-6'), { order: 0 });

  const revealGroups: Array<{
    selector: string;
    type: string;
    step?: number;
    order?: number;
    immediate?: boolean;
  }> = [
    { selector: '.modern-service-hero-section app-service-orbit-visual', type: 'media', order: 2, immediate: true },
    { selector: '.modern-service-cards-section .modern-service-card', type: 'card', step: 0.1 },
    { selector: '.modern-service-why-section .glass-card', type: 'card', order: 1 }
  ];

  revealGroups.forEach(({ selector, type, step = 0, order, immediate }) => {
    const elements = Array.from(root.querySelectorAll<HTMLElement>(selector));
    elements.forEach((element, index) => {
      if (element.closest('.section-head')) return;

      element.classList.add('reveal-on-scroll');
      if (!element.dataset['reveal']) {
        element.dataset['reveal'] = type;
      }
      if (immediate) {
        element.dataset['revealTrigger'] = 'immediate';
      }
      if (!element.style.getPropertyValue('--reveal-order')) {
        const revealOrder =
          order !== undefined ? order : Math.min(8, Number((index * step).toFixed(2)));
        element.style.setProperty('--reveal-order', String(revealOrder));
      }
    });
  });
}

export async function initLegacyServiceDetailReveal(
  root: HTMLElement,
  cleanups: Array<() => void>
): Promise<void> {
  prepareLegacyServiceDetailRevealElements(root);
  await initPageScrollReveal(root, cleanups);
}
